import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './IntegrationApplicationMasterData.css';

interface ApplicationPortfolioStats {
  totalApplications: number;
  dependencies: {
    low: number;
    moderate: number;
    high: number;
  };
  distribution: {
    'Retire': number;
    'Re-Platform': number;
    'Re-Design': number;
    'Retain': number;
  };
  integrationPoints: number;
  dataCompleteness: {
    withTechnologyStack: number;
    withInfrastructure: number;
    withDependencies: number;
  };
}

interface ApplicationData {
  application_id_code: string;
  name: string;
  technology_stack?: string;
  it_hardware_infrastructure?: string;
  integration_dependencies?: string;
  recommendation?: string;
}

const ApplicationPortfolio: React.FC = () => {
  const [stats, setStats] = useState<ApplicationPortfolioStats>({
    totalApplications: 0,
    dependencies: { low: 0, moderate: 0, high: 0 },
    distribution: { 'Retire': 0, 'Re-Platform': 0, 'Re-Design': 0, 'Retain': 0 },
    integrationPoints: 0,
    dataCompleteness: { withTechnologyStack: 0, withInfrastructure: 0, withDependencies: 0 }
  });
  const [applications, setApplications] = useState<ApplicationData[]>([]);
  const [loading, setLoading] = useState(true);

  const loadStats = async () => {
    setLoading(true);
    try {
      // Fetch data from the applications-master endpoint
      const [appsResponse] = await Promise.all([
        fetch('/api/applications-master')
      ]);

      if (appsResponse.ok) {
        const appsData = await appsResponse.json();
        if (appsData.success) {
          const applicationsData = appsData.data || [];
          setApplications(applicationsData);

          setStats({
            totalApplications: applicationsData.length,
            dependencies: {
              low: applicationsData.filter((app: any) => app.integration_dependencies === 'Low').length,
              moderate: applicationsData.filter((app: any) => app.integration_dependencies === 'Moderate').length,
              high: applicationsData.filter((app: any) => app.integration_dependencies === 'High').length,
            },
            distribution: {
              'Retire': applicationsData.filter((app: any) => app.recommendation === 'Retire').length,
              'Re-Platform': applicationsData.filter((app: any) => app.recommendation === 'Re-Platform').length,
              'Re-Design': applicationsData.filter((app: any) => app.recommendation === 'Re-Design').length,
              'Retain': applicationsData.filter((app: any) => app.recommendation === 'Retain').length,
            },
            integrationPoints: applicationsData.reduce((sum: number, app: any) =>
              sum + (app.integration_dependencies === 'High' ? 3 :
                     app.integration_dependencies === 'Moderate' ? 2 : 1), 0),
            dataCompleteness: {
              withTechnologyStack: applicationsData.filter((app: any) => app.technology_stack && app.technology_stack.trim() !== '').length,
              withInfrastructure: applicationsData.filter((app: any) => app.it_hardware_infrastructure && app.it_hardware_infrastructure.trim() !== '').length,
              withDependencies: applicationsData.filter((app: any) => app.integration_dependencies && app.integration_dependencies.trim() !== '').length,
            }
          });
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const downloadApplicationsData = () => {
    if (applications.length === 0) {
      alert('No applications data to download');
      return;
    }

    const headers = [
      'Application ID',
      'Application Name',
      'Technology Stack',
      'IT Hardware Infrastructure',
      'Integration Dependencies',
      'Recommendation'
    ];
    const csvRows = [headers.join(',')];

    applications.forEach((app: any) => {
      const row = [
        `"${(app.application_id_code || '').replace(/"/g, '""')}"`,
        `"${(app.name || '').replace(/"/g, '""')}"`,
        `"${(app.technology_stack || '').replace(/"/g, '""')}"`,
        `"${(app.it_hardware_infrastructure || '').replace(/"/g, '""')}"`,
        `"${(app.integration_dependencies || '').replace(/"/g, '""')}"`,
        `"${(app.recommendation || '').replace(/"/g, '""')}"`
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `applications_portfolio_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    loadStats();
  }, []);

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">Application Portfolio</h1>
            <p className="page-header__subtitle">
              Comprehensive overview of applications in the integration framework
            </p>
          </div>
          <div className="page-header__actions">
            {applications.length > 0 && (
              <button
                className="btn btn-outline-success"
                onClick={downloadApplicationsData}
                title="Download applications portfolio data as CSV"
                style={{ border: '2px solid #28a745' }}
              >
                📥 Download Data
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="master-data-container">

      {/* Dependencies */}
      <div className="md-card">
        <div className="md-card-content">
          <h2 style={{ marginBottom: '24px', color: '#1976d2' }}>🔗 Dependencies</h2>
          <div className="md-stats-grid">
            <div className="md-card md-card--elevated">
              <div className="md-card-content" style={{ textAlign: 'center' }}>
                <div className="md-stat-primary">{stats.totalApplications}</div>
                <div className="md-stat-label" style={{ fontSize: '12px', whiteSpace: 'nowrap', textAlign: 'center' }}>Total Apps</div>
              </div>
            </div>
            
            <div className="md-card md-card--elevated">
              <div className="md-card-content" style={{ textAlign: 'center' }}>
                <div className="md-stat-primary" style={{ color: '#10b981' }}>
                  {stats.dependencies.low}
                </div>
                <div className="md-stat-label" style={{ fontSize: '12px', whiteSpace: 'nowrap', textAlign: 'center' }}>Low Dependencies</div>
              </div>
            </div>
            
            <div className="md-card md-card--elevated">
              <div className="md-card-content" style={{ textAlign: 'center' }}>
                <div className="md-stat-primary" style={{ color: '#f59e0b' }}>
                  {stats.dependencies.moderate}
                </div>
                <div className="md-stat-label" style={{ fontSize: '12px', whiteSpace: 'nowrap', textAlign: 'center' }}>Moderate Dependencies</div>
              </div>
            </div>
            
            <div className="md-card md-card--elevated">
              <div className="md-card-content" style={{ textAlign: 'center' }}>
                <div className="md-stat-primary" style={{ color: '#dc2626' }}>
                  {stats.dependencies.high}
                </div>
                <div className="md-stat-label" style={{ fontSize: '12px', whiteSpace: 'nowrap', textAlign: 'center' }}>High Dependencies</div>
              </div>
            </div>
            
            <div className="md-card md-card--elevated">
              <div className="md-card-content" style={{ textAlign: 'center' }}>
                <div className="md-stat-primary" style={{ color: '#3b82f6' }}>
                  {stats.integrationPoints}
                </div>
                <div className="md-stat-label" style={{ fontSize: '12px', whiteSpace: 'nowrap', textAlign: 'center' }}>Integration Points</div>
              </div>
            </div>
            
            <div className="md-card md-card--elevated">
              <div className="md-card-content" style={{ textAlign: 'center' }}>
                <div className="md-stat-primary" style={{ color: '#8b5cf6' }}>
                  {stats.dataCompleteness.withDependencies}
                </div>
                <div className="md-stat-label" style={{ fontSize: '12px', whiteSpace: 'nowrap', textAlign: 'center' }}>With Dependencies</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Application Portfolio Distribution */}
      <div className="md-card">
        <div className="md-card-content">
          <h2 style={{ marginBottom: '24px', color: '#1976d2' }}>🎯 Application Portfolio Distribution</h2>
          <div className="md-stats-grid">
            <div className="md-card md-card--elevated">
              <div className="md-card-content">
                <div className="md-stat-primary" style={{ color: '#dc2626' }}>
                  {stats.distribution['Retire']}
                </div>
                <div className="md-stat-label" style={{ fontSize: '14px' }}>🗑️ Retire</div>
                <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                  {stats.totalApplications > 0 
                    ? `${Math.round((stats.distribution['Retire'] / stats.totalApplications) * 100)}% of portfolio` 
                    : '0% of portfolio'}
                </div>
              </div>
            </div>
            
            <div className="md-card md-card--elevated">
              <div className="md-card-content">
                <div className="md-stat-primary" style={{ color: '#f59e0b' }}>
                  {stats.distribution['Re-Platform']}
                </div>
                <div className="md-stat-label" style={{ fontSize: '14px' }}>🔄 Re-Platform</div>
                <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                  {stats.totalApplications > 0 
                    ? `${Math.round((stats.distribution['Re-Platform'] / stats.totalApplications) * 100)}% of portfolio` 
                    : '0% of portfolio'}
                </div>
              </div>
            </div>
            
            <div className="md-card md-card--elevated">
              <div className="md-card-content">
                <div className="md-stat-primary" style={{ color: '#3b82f6' }}>
                  {stats.distribution['Re-Design']}
                </div>
                <div className="md-stat-label" style={{ fontSize: '14px' }}>🎨 Re-Design</div>
                <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                  {stats.totalApplications > 0 
                    ? `${Math.round((stats.distribution['Re-Design'] / stats.totalApplications) * 100)}% of portfolio` 
                    : '0% of portfolio'}
                </div>
              </div>
            </div>
            
            <div className="md-card md-card--elevated">
              <div className="md-card-content">
                <div className="md-stat-primary" style={{ color: '#10b981' }}>
                  {stats.distribution['Retain']}
                </div>
                <div className="md-stat-label" style={{ fontSize: '14px' }}>✅ Retain</div>
                <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                  {stats.totalApplications > 0 
                    ? `${Math.round((stats.distribution['Retain'] / stats.totalApplications) * 100)}% of portfolio` 
                    : '0% of portfolio'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Data Completeness Overview */}
      <div className="md-card">
        <div className="md-card-content">
          <h2 style={{ marginBottom: '24px', color: '#1976d2' }}>📊 Data Completeness</h2>
          <div className="md-stats-grid">
            <div className="md-card md-card--elevated">
              <div className="md-card-content">
                <div className="md-stat-primary" style={{ color: '#10b981' }}>
                  {stats.dataCompleteness.withTechnologyStack}
                </div>
                <div className="md-stat-label" style={{ fontSize: '14px' }}>💻 Technology Stack</div>
                <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                  {stats.totalApplications > 0 
                    ? `${Math.round((stats.dataCompleteness.withTechnologyStack / stats.totalApplications) * 100)}% of portfolio` 
                    : '0% of portfolio'}
                </div>
              </div>
            </div>
            
            <div className="md-card md-card--elevated">
              <div className="md-card-content">
                <div className="md-stat-primary" style={{ color: '#3b82f6' }}>
                  {stats.dataCompleteness.withInfrastructure}
                </div>
                <div className="md-stat-label" style={{ fontSize: '14px' }}>🏗️ Infrastructure</div>
                <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                  {stats.totalApplications > 0 
                    ? `${Math.round((stats.dataCompleteness.withInfrastructure / stats.totalApplications) * 100)}% of portfolio` 
                    : '0% of portfolio'}
                </div>
              </div>
            </div>
            
            <div className="md-card md-card--elevated">
              <div className="md-card-content">
                <div className="md-stat-primary" style={{ color: '#8b5cf6' }}>
                  {stats.dataCompleteness.withDependencies}
                </div>
                <div className="md-stat-label" style={{ fontSize: '14px' }}>🔗 Dependencies</div>
                <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                  {stats.totalApplications > 0 
                    ? `${Math.round((stats.dataCompleteness.withDependencies / stats.totalApplications) * 100)}% of portfolio` 
                    : '0% of portfolio'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Navigation Cards */}
      <div className="md-card">
        <div className="md-card-content">
          <h2 style={{ marginBottom: '24px', color: '#1976d2' }}>🚀 Quick Actions</h2>
          <div className="md-stats-grid">
            <Link to="/integration-framework/application-master-data" className="md-card md-card--elevated" style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
              <div className="md-card-content">
                <div className="md-stat-primary" style={{ color: '#6366f1', fontSize: '24px' }}>
                  📱
                </div>
                <div className="md-stat-label" style={{ fontSize: '14px' }}>Application Master Data</div>
                <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                  Manage application information
                </div>
              </div>
            </Link>
            
            <Link to="/integration-framework/application-dependencies" className="md-card md-card--elevated" style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
              <div className="md-card-content">
                <div className="md-stat-primary" style={{ color: '#8b5cf6', fontSize: '24px' }}>
                  🔗
                </div>
                <div className="md-stat-label" style={{ fontSize: '14px' }}>Application Dependencies</div>
                <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                  Define integration relationships
                </div>
              </div>
            </Link>
            
            <Link to="/integration-framework/integration-points" className="md-card md-card--elevated" style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
              <div className="md-card-content">
                <div className="md-stat-primary" style={{ color: '#06b6d4', fontSize: '24px' }}>
                  🔀
                </div>
                <div className="md-stat-label" style={{ fontSize: '14px' }}>Integration Points</div>
                <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                  Configure integration endpoints
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>


      {loading && (
        <div className="empty-state">
          <div className="empty-state-icon">⏳</div>
          <h3>Loading Application Portfolio Data</h3>
          <p>Please wait while we fetch the latest portfolio statistics...</p>
        </div>
      )}

      {!loading && stats.totalApplications === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">📋</div>
          <h3>No Applications Available</h3>
          <p>No applications have been added to the portfolio yet. Add applications via the Application Master Data page to get started.</p>
          <div className="empty-state-actions">
            <button
              className="btn btn-primary"
              onClick={() => window.location.href = '/integration-framework/application-master-data'}
            >
              Go to Application Master Data
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default ApplicationPortfolio;